import { getCampaignsQueryOptions, useUsageCampaign } from "@/api/campaign.api";
import { createTypedTable } from "@/components/generic/Table";
import { MainWindow, PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { parseDate } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DateValue,
  SortDescriptor,
  type Selection,
} from "react-aria-components";

import { ActivityStatisticsParams } from "@/api/dashboard.api";
import { DateRangePicker } from "@/components/generic/DateRangePicker";
import { MultiSelect, useMultiSelect } from "@/components/generic/MultiSelect";
import { TagGroupBox, TagItem } from "@/components/generic/TagGroup";
import { StickyElement } from "@/components/layout/StickyElement";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import { formatDate } from "@/utils/dates";
import { AnimatePresence, motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

type RangeValue<T> = {
  start: T;
  end: T;
};

type UsageDataByDate = {
  id: string | number;
  date: string;
  leads: number;
  companies: number;
  liveNumbers: number;
  contacts: number;
  refunded: number;
  totalUsage: number;
};

type UsageDataByCampaign = {
  id: string | number;
  campaignId: number;
  campaignName: string;
  leads: number;
  companies: number;
  liveNumbers: number;
  contacts: number;
  refunded: number;
  totalUsage: number;
};

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/subscription/_subscription/credit-usage",
)({
  component: RouteComponent,
  loader: async ({ context }) => {
    const campaignsQueryOptions = getCampaignsQueryOptions({
      includeStatistics: true,
    });
    const campaigns = await context.queryClient.ensureQueryData(
      campaignsQueryOptions,
    );

    await context.queryClient.invalidateQueries({
      queryKey: ["usageCampaign"],
    });

    return {
      useCampaignsQuery: () =>
        useQuery({
          ...campaignsQueryOptions,
          initialData: campaigns,
        }),
      crumb: "subscriptions",
    };
  },
});

function RouteComponent() {
  const dateValueToISO = useCallback((dateValue: DateValue | null): string => {
    if (!dateValue) return new Date().toISOString();

    const utcDate = new Date(
      Date.UTC(dateValue.year, dateValue.month - 1, dateValue.day, 0, 0, 0, 0),
    );

    return utcDate.toISOString();
  }, []);

  const { useCampaignsQuery } = Route.useLoaderData();
  const context = Route.useRouteContext();

  const created = context.user.created;

  const today = new Date();

  const weekAgo = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 6,
  );

  const [selectedTable, setSelectedTable] = useState<Selection>(
    new Set(["campaign"]),
  );
  const [dateRange, setDateRange] = useState<RangeValue<DateValue>>({
    start: parseDate(weekAgo.toISOString().split("T")[0]),
    end: parseDate(today.toISOString().split("T")[0]),
  });
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "date",
    direction: "ascending",
  });

  const [sortDescriptorCampaign, setSortDescriptorCampaign] =
    useState<SortDescriptor>({
      column: "campaignName",
      direction: "descending",
    });

  const { data: campaigns } = useCampaignsQuery();

  const campaignMultiSelect = useMultiSelect({
    items: useMemo(
      () =>
        campaigns
          ?.map((campaign) => ({
            key: campaign?.id?.toString(),
            label: campaign.name,
            value: campaign.id,
          }))
          .sort((a, b) => {
            return a.label?.localeCompare(b.label || "") || 0;
          }) ?? [],
      [campaigns],
    ),
  });
  const handleDateRangeChange = (range: RangeValue<DateValue> | null) => {
    if (!range) return;

    const today = parseDate(new Date().toISOString().split("T")[0]);
    const MAX_RANGE_DAYS = 30;
    const MIN_VALUE = parseDate(new Date(created).toISOString().split("T")[0]);

    const clampToToday = (date: DateValue): DateValue =>
      date.compare(today) > 0 ? today : date;

    const clampedStart = clampToToday(range.start);
    const clampedEnd = clampToToday(range.end);

    const isStartAfterEnd = clampedStart.compare(clampedEnd) > 0;

    const rangeDays = Math.abs(clampedStart.compare(clampedEnd));
    const isRangeTooLarge = rangeDays > MAX_RANGE_DAYS;

    const isMinValueViolated = MIN_VALUE > clampedStart;

    if (isRangeTooLarge) {
      setDateRange({
        start: clampedEnd.subtract({ days: MAX_RANGE_DAYS }),
        end: clampedEnd,
      });
      return;
    }

    if (isStartAfterEnd) {
      setDateRange({ start: clampedEnd, end: clampedEnd });
      return;
    }

    if (isMinValueViolated) {
      setDateRange({
        start: MIN_VALUE,
        end: MIN_VALUE.add({ days: MAX_RANGE_DAYS }),
      });
      return;
    }

    setDateRange({ start: clampedStart, end: clampedEnd });
  };

  const handleCampaignChange = useCallback(
    (keys: Selection) => {
      campaignMultiSelect.setSelectedKeys(keys);
    },
    [campaignMultiSelect],
  );

  const apiParams: Partial<ActivityStatisticsParams> = useMemo(() => {
    const campaignIds = campaignMultiSelect.selectedValues
      .filter((id) => id)
      .map((id) => Number(id))
      .filter((n) => !Number.isNaN(n));

    const startDate = new Date(dateValueToISO(dateRange.start));
    const endDate = new Date(dateValueToISO(dateRange.end));
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const periodCount = diffDays === 0 ? 1 : diffDays;

    return {
      period: "day",
      from: dateValueToISO(dateRange.start),
      to: periodCount,
      campaignIds: campaignIds.length > 0 ? campaignIds : [],
    };
  }, [dateRange, campaignMultiSelect.selectedValues, dateValueToISO]);

  const {
    data: rawUsageData,
    isFetching,
    isLoading,
    isPending,
  } = useUsageCampaign(apiParams);

  const isDateView = selectedTable instanceof Set && selectedTable.has("date");

  const transformedData = useMemo(() => {
    if (!rawUsageData) {
      return [];
    }

    if (isDateView) {
      const dateData: UsageDataByDate[] = [];

      if (rawUsageData.periods && Array.isArray(rawUsageData.periods)) {
        rawUsageData.periods.forEach((period, periodIndex: number) => {
          if (
            !period.counts ||
            !Array.isArray(period.counts) ||
            period.counts.length === 0
          ) {
            return;
          }

          const startDate = new Date(period.periodStart);
          const dateKey = formatDate(startDate);

          const periodTotals = {
            leads: 0,
            companies: 0,
            liveNumbers: 0,
            contacts: 0,
            creditsUsed: 0,
            liveNumberAuto: 0,
            liveNumberAdHoc: 0,
            contactsAuto: 0,
            contactsAdHoc: 0,
            refunded: 0,
            totalUsage: 0,
          };

          period.counts.forEach((campaignData) => {
            periodTotals.leads += campaignData.count || 0;
            periodTotals.creditsUsed += campaignData.creditsUsed || 0;
            periodTotals.liveNumberAuto += campaignData.liveNumberAuto || 0;
            periodTotals.liveNumberAdHoc += campaignData.liveNumberAdHoc || 0;
            periodTotals.contactsAuto += campaignData.contactsAuto || 0;
            periodTotals.contactsAdHoc += campaignData.contactsAdHoc || 0;
            periodTotals.refunded += campaignData.refunded || 0;
          });

          periodTotals.companies = periodTotals.creditsUsed;
          periodTotals.liveNumbers =
            periodTotals.liveNumberAuto + periodTotals.liveNumberAdHoc;
          periodTotals.contacts =
            periodTotals.contactsAuto + periodTotals.contactsAdHoc;
          periodTotals.totalUsage =
            periodTotals.creditsUsed +
            periodTotals.liveNumbers +
            periodTotals.contacts;

          const entry: UsageDataByDate = {
            id: periodIndex,
            date: dateKey,
            leads: periodTotals.leads,
            companies: periodTotals.companies,
            liveNumbers: periodTotals.liveNumbers,
            contacts: periodTotals.contacts,
            refunded: periodTotals.refunded,
            totalUsage: periodTotals.totalUsage - periodTotals.refunded,
          };

          dateData.push(entry);
        });
      }

      return dateData.reverse();
    } else {
      if (!campaigns) return null;

      const campaignMap = new Map<number, UsageDataByCampaign>();

      if (rawUsageData.periods && Array.isArray(rawUsageData.periods)) {
        rawUsageData.periods.forEach((period) => {
          if (!period.counts || !Array.isArray(period.counts)) return;

          period.counts.forEach((campaignData) => {
            const campaignId = campaignData.campaignId;

            if (!campaignMap.has(campaignId)) {
              const campaign = campaigns.find((c) => c?.id === campaignId);
              campaignMap.set(campaignId, {
                id: campaignId,
                campaignId,
                campaignName: campaign?.name || `Campaign ${campaignId}`,
                leads: 0,
                companies: 0,
                liveNumbers: 0,
                contacts: 0,
                refunded: 0,
                totalUsage: 0,
              });
            }

            const existing = campaignMap.get(campaignId);
            if (!existing) return;

            existing.leads += campaignData.count || 0;
            existing.companies += campaignData.creditsUsed || 0;
            existing.liveNumbers +=
              (campaignData.liveNumberAuto || 0) +
              (campaignData.liveNumberAdHoc || 0);
            existing.contacts +=
              (campaignData.contactsAuto || 0) +
              (campaignData.contactsAdHoc || 0);
            existing.refunded += campaignData.refunded || 0;
            existing.totalUsage +=
              (campaignData.creditsUsed || 0) +
              existing.liveNumbers +
              existing.contacts;
          });
        });
      }
      const result = Array.from(campaignMap.values()).map((campaign) => ({
        ...campaign,
        totalUsage: campaign.totalUsage - campaign.refunded,
      }));
      return result.reverse();
    }
  }, [rawUsageData, isDateView, campaigns]);

  const dateTypedTable = createTypedTable<UsageDataByDate>();
  const campaignTypedTable = createTypedTable<UsageDataByCampaign>();

  const hasCompaniesData = useMemo(() => {
    if (!transformedData || !Array.isArray(transformedData)) return false;
    return transformedData.some((item) => item.companies > 0);
  }, [transformedData]);

  const dateColumns = dateTypedTable.createColumns([
    {
      id: "date",
      name: "Date",
      isRowHeader: false,
      width: 240,
      renderCell: (item) => (
        <span className="text-xs font-bold">{item.date}</span>
      ),
      renderGroupHeader: () => (
        <span className="text-theme-on-glass absolute bottom-8 text-left">
          Overblik
        </span>
      ),
      headerClassName: "h-15 pt-6",
    },
    {
      id: "leads",
      name: "Leads",
      width: 90,
      renderCell: (item) => (
        <span className="pr-10 text-right text-xs font-bold">{item.leads}</span>
      ),
      headerClassName:
        "text-right flex justify-end w-full pt-6 pr-10  h-15 border-theme-outline",
      rowClassName: "border-theme-outline",
    },

    {
      id: "totalUsage",
      name: "Credit forbrug",
      width: 120,
      renderCell: (item) => (
        <span className="block w-full pr-15 text-right text-xs font-bold">
          {item.totalUsage}
        </span>
      ),
      headerClassName:
        "text-right flex justify-end w-full pt-6 pr-15 h-15 border-theme-outline",
    },
    ...(hasCompaniesData
      ? [
          {
            id: "companies" as const,
            name: "Firmaer",
            width: 30,
            renderCell: (item: UsageDataByDate) => (
              <span className="block w-full text-left text-xs font-medium">
                {item.companies}
              </span>
            ),
            className: "ml-10 text-left",
            headerClassName: "text-left h-15 pt-6",
            renderGroupHeader: () => (
              <span className="text-theme-on-glass absolute bottom-8 text-left">
                Detaljeret credit forbrug
              </span>
            ),
          },
        ]
      : []),
    {
      id: "liveNumbers",
      name: "Live numre",
      width: 70,
      renderCell: (item) => (
        <span className="block w-full text-right text-xs font-medium">
          {item.liveNumbers}
        </span>
      ),
      headerClassName: "text-right pt-6 h-15",
      ...(!hasCompaniesData && {
        className: "ml-10 text-left",
        headerClassName: "text-left h-15 pt-6",
        renderGroupHeader: () => (
          <span className="text-theme-on-glass absolute bottom-8 text-left">
            Detaljeret credit forbrug
          </span>
        ),
      }),
    },
    {
      id: "contacts",
      name: "Kontaktpersoner",
      width: 100,
      renderCell: (item) => (
        <span className="block w-full text-right text-xs font-medium">
          {item.contacts}
        </span>
      ),
      headerClassName: "text-right pt-6 h-15",
    },
    {
      id: "refunded",
      name: "Refunderet",
      width: 100,
      renderCell: (item) => {
        if (item.refunded === 0) {
          return (
            <span className="block w-full text-right text-xs font-medium">
              {item.refunded}
            </span>
          );
        }
        return (
          <span
            className={twMerge(
              "block w-full text-right text-xs font-medium",
              item.refunded > 0 && "text-green-600 dark:text-green-400",
            )}
          >
            {item.refunded > 0 && `+ ${item.refunded}`}
          </span>
        );
      },

      headerClassName: "text-right pt-6 h-15",
    },
  ]);

  const campaignColumns = campaignTypedTable.createColumns([
    {
      id: "campaignName",
      name: "Kampagne",
      isRowHeader: false,
      width: 240,
      renderCell: (item) => (
        <span className="text-xs font-bold">{item.campaignName}</span>
      ),
      renderGroupHeader: () => (
        <span className="text-theme-on-glass absolute bottom-8 text-left">
          Overblik
        </span>
      ),
      headerClassName: "h-15 pt-6",
    },
    {
      id: "leads",
      name: "Leads",
      width: 90,
      renderCell: (item) => (
        <span className="pr-10 text-right text-xs font-bold">{item.leads}</span>
      ),
      headerClassName:
        "text-right flex justify-end w-full pt-6 pr-10  h-15 border-theme-outline",
      rowClassName: "border-theme-outline",
    },

    {
      id: "totalUsage",
      name: "Credit forbrug",
      width: 120,
      renderCell: (item) => (
        <span className="block w-full pr-15 text-right text-xs font-bold">
          {item.totalUsage}
        </span>
      ),
      headerClassName:
        "text-right flex justify-end w-full pt-6 pr-15 h-15 border-theme-outline",
    },
    ...(hasCompaniesData
      ? [
          {
            id: "companies" as const,
            name: "Firmaer",
            width: 30,
            renderCell: (item: UsageDataByCampaign) => (
              <span className="block w-full text-left text-xs font-medium">
                {item.companies}
              </span>
            ),
            className: "ml-10 text-left",
            headerClassName: "text-left h-15 pt-6",
            renderGroupHeader: () => (
              <span className="text-theme-on-glass absolute bottom-8 text-left">
                Detaljeret credit forbrug
              </span>
            ),
          },
        ]
      : []),
    {
      id: "liveNumbers",
      name: "Live numre",
      width: 70,
      renderCell: (item) => (
        <span className="block w-full text-right text-xs font-medium">
          {item.liveNumbers}
        </span>
      ),
      headerClassName: "text-right pt-6 h-15",
      ...(!hasCompaniesData && {
        className: "ml-10 text-left",
        headerClassName: "text-left h-15 pt-6",
        renderGroupHeader: () => (
          <span className="text-theme-on-glass absolute bottom-8 text-left">
            Detaljeret credit forbrug
          </span>
        ),
      }),
    },
    {
      id: "contacts",
      name: "Kontaktpersoner",
      width: 100,
      renderCell: (item) => (
        <span className="block w-full text-right text-xs font-medium">
          {item.contacts}
        </span>
      ),
      headerClassName: "text-right pt-6 h-15",
    },
    {
      id: "refunded",
      name: "Refunderet",
      width: 100,
      renderCell: (item) => {
        if (item.refunded === 0) {
          return (
            <span className="block w-full text-right text-xs font-medium">
              {item.refunded}
            </span>
          );
        }
        return (
          <span
            className={twMerge(
              "block w-full text-right text-xs font-medium",
              item.refunded > 0 && "text-green-600 dark:text-green-400",
            )}
          >
            {item.refunded > 0 && `+ ${item.refunded}`}
          </span>
        );
      },
      headerClassName: "text-right pt-6 h-15",
    },
  ]);
  const dateData = transformedData as UsageDataByDate[];
  const campaignData = transformedData as UsageDataByCampaign[];

  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateLinePosition = () => {
      if (!bodyScrollRef.current || !lineRef.current) return;

      const targetColumn = bodyScrollRef.current.querySelector(
        '[aria-colindex="3"]',
      );
      if (!targetColumn) return;

      const rect = targetColumn.getBoundingClientRect();
      const parentRect = bodyScrollRef.current.getBoundingClientRect();
      const leftPosition = rect.right - parentRect.left;

      lineRef.current.style.left = `${leftPosition}px`;
    };

    updateLinePosition();

    const timeoutId = setTimeout(updateLinePosition, 10);
    const rafId = requestAnimationFrame(updateLinePosition);

    const scrollElement = bodyScrollRef.current;
    scrollElement?.addEventListener("scroll", updateLinePosition);
    window.addEventListener("resize", updateLinePosition);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      scrollElement?.removeEventListener("scroll", updateLinePosition);
      window.removeEventListener("resize", updateLinePosition);
    };
  }, [
    dateData,
    campaignData,
    campaigns,
    isDateView,
    selectedTable,
    isFetching,
    isLoading,
    isPending,
  ]);

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  useHorizontalScrollSync(headerScrollRef, bodyScrollRef, [
    dateData,
    campaignData,
    campaigns,
    isDateView,
    selectedTable,
    isFetching,
    isLoading,
    isPending,
  ]);

  const isLoadingData = isFetching || isLoading || isPending;

  const dataIndicator = () => {
    return (
      <div className="grid grow px-14 *:col-start-1 *:row-start-1">
        <AnimatePresence>
          {(() => {
            let key = "";
            let text = "";

            if (isLoadingData) {
              key = "loading";
              text = "Indlæser";
            } else if (!dateData.length) {
              key = "no-results";
              text = "Ingen leads modtaget i den valgte periode";
            }

            if (!key) return null;

            return (
              <motion.div
                key={key}
                className="flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <span
                  className={twMerge(
                    "text-theme-on-background-variant text-center text-xs font-medium",
                    key === "loading" && "animate-pulse",
                  )}
                >
                  {text}
                </span>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <MainWindow className="flex h-full w-full flex-col" theme="none">
      <PageHeader
        backLinkOptions={{
          from: Route.fullPath,
          to: "/settings/subscription",
        }}
        breadcrumbs={{
          className: "uppercase",
          from: Route.fullPath,
          fromCrumb: "Creditforbrug",
        }}
        className="z-60"
        actions={
          <div className="flex gap-4">
            <DateRangePicker
              maxValue={parseDate(new Date().toISOString().split("T")[0])}
              value={dateRange}
              minValue={parseDate(
                new Date(created).toISOString().split("T")[0],
              )}
              onChange={handleDateRangeChange}
              aria-label="Period (from-to)"
            />
            <MultiSelect
              placeholder="Alle kampagner"
              buttonClassName="min-h-13.5! py-2 px-3"
              items={campaignMultiSelect.items}
              selectedKeys={campaignMultiSelect.selectedKeys}
              onSelectionChange={handleCampaignChange}
            />
          </div>
        }
      />
      <StickyElement
        className="z-60 h-6"
        stickyProps={{
          className: "bg-theme-background rounded-t-lg",
        }}
      />

      <PageContent className="flex grow flex-col">
        <div className="mb-6 flex items-center justify-between px-14">
          <p className="text-lg font-medium">Forbrugshistorik</p>
          <TagGroupBox
            defaultSelectedKeys={["campaign"]}
            selectedKeys={selectedTable}
            onSelectionChange={setSelectedTable}
            selectionMode="single"
            selectionBehavior="replace"
          >
            <TagItem id="campaign">Kampagne</TagItem>
            <TagItem id="date">Dato</TagItem>
          </TagGroupBox>
        </div>

        <div className="flex flex-1 flex-col">
          {isLoadingData || dateData.length === 0 ? (
            dataIndicator()
          ) : isDateView ? (
            <dateTypedTable.Table
              className="relative h-full max-w-full pb-10"
              columns={dateColumns}
              items={dateData}
              sortDescriptor={sortDescriptor}
              setSortDescriptor={setSortDescriptor}
            >
              <StickyElement
                ref={headerScrollRef}
                className="shrink-0 overflow-x-auto"
                id="scroll-headers"
                stickyProps={{ className: "bg-theme-background" }}
              >
                <dateTypedTable.TableHeader />
              </StickyElement>
              <div
                ref={lineRef}
                className="bg-theme-outline pointer-events-none absolute top-3 bottom-6 z-50 w-px"
              />
              <dateTypedTable.TableBody
                id="scroll-headers"
                ref={bodyScrollRef}
                className="min-w-0 flex-1 overflow-x-scroll"
              ></dateTypedTable.TableBody>
            </dateTypedTable.Table>
          ) : (
            <>
              <campaignTypedTable.Table
                className="relative h-full max-w-full pb-10"
                columns={campaignColumns}
                items={campaignData}
                sortDescriptor={sortDescriptorCampaign}
                setSortDescriptor={setSortDescriptorCampaign}
              >
                <StickyElement
                  ref={headerScrollRef}
                  className="overflow-x-auto"
                  stickyProps={{
                    className: "bg-theme-background ",
                  }}
                  id="scroll-headers"
                >
                  <campaignTypedTable.TableHeader />
                </StickyElement>

                <div
                  ref={lineRef}
                  className="bg-theme-outline pointer-events-none absolute top-3 bottom-6 z-50 w-px"
                />
                <campaignTypedTable.TableBody
                  id="scroll-headers"
                  ref={bodyScrollRef}
                  className="min-w-0 flex-1 overflow-x-auto"
                />
              </campaignTypedTable.Table>
            </>
          )}
        </div>
      </PageContent>
    </MainWindow>
  );
}
